import React from 'react';
import { 
  Box, 
  Avatar, 
  Typography, 
  TextField, 
  IconButton, 
  Collapse, 
  useTheme 
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Fullscreen as FullscreenIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

const ChatWidget = ({ 
  bot, 
  chatHistory, 
  message, 
  setMessage, 
  sending, 
  handleSend, 
  chatContainerRef, 
  minimized, 
  toggleMinimize, 
  expanded, 
  toggleExpanded, 
  toggleChat, 
  floating = false,
  chatOpen = true
}) => {
  const theme = useTheme();
  
  // If not floating, render a static version for the right side
  if (!floating) {
    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 80px rgba(0,0,0,0.3), 0 0 20px rgba(76, 133, 240, 0.2)',
          animation: 'fadeInUp 0.8s ease-out forwards 0.3s',
          opacity: 0,
          transform: 'scale(1.02)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px',
            zIndex: 2,
            pointerEvents: 'none'
          }
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '600px', 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#fff',
            zIndex: 1,
          }}
        >
          <ChatHeader 
            bot={bot} 
            minimized={false} 
            toggleMinimize={() => {}} 
            toggleExpanded={() => {}} 
            toggleChat={() => {}} 
          />
          
          <ChatMessages 
            chatHistory={chatHistory} 
            chatContainerRef={chatContainerRef} 
            sending={sending} 
            bot={bot}
            formatResponse={(text) => text}
          />
          
          <ChatInput 
            message={message} 
            setMessage={setMessage} 
            handleSend={handleSend} 
            sending={sending} 
            bot={bot} 
          />
        </Box>
      </Box>
    );
  }
  
  // Floating chat widget for mobile/responsive use
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: expanded ? '90vw' : '350px',
        maxWidth: expanded ? '900px' : '350px',
        height: minimized ? '60px' : expanded ? '80vh' : '500px',
        backgroundColor: 'rgba(22, 22, 26, 0.95)',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        opacity: chatOpen ? 1 : 0,
        visibility: chatOpen ? 'visible' : 'hidden',
        transform: chatOpen ? 'translateY(0)' : 'translateY(20px)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        '& .betfi-badge': {
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: '#FF4500', // BetFI brand color
          color: 'white',
          padding: '4px 10px',
          borderRadius: '12px',
          fontWeight: 'bold',
          textDecoration: 'none',
          margin: '3px 0',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease',
          fontSize: '0.9rem',
          '&:hover': {
            backgroundColor: '#FF6536',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          }
        },
        '& .betfi-icon': {
          marginRight: '5px',
          fontSize: '1.1em',
        },
        '& .betfi-arrow': {
          marginLeft: '5px',
          fontWeight: 'bold',
        }
      }}
    >
      {minimized ? (
        // Minimized circular badge - rendered separately when minimized
        <Box
          sx={{
            position: 'fixed', // Fixed position
            bottom: chatOpen ? 20 : -500, // Position at bottom
            right: 20, // Position at right
            width: 85, // Increased size by 50%
            height: 85, // Increased size by 50%
            borderRadius: '50%',
            backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
            display: chatOpen ? 'flex' : 'none', // Only show when chat is open
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: `0 8px 24px ${bot?.customizations?.primaryColor || theme.palette.primary.main}60`,
            animation: 'pulse 2s infinite ease-in-out',
            transition: 'all 0.3s ease',
            zIndex: 1000, // High z-index to ensure visibility
            transform: 'scale(1)', // Make sure it's visible at normal size
            opacity: chatOpen ? 1 : 0,
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: `0 8px 28px ${bot?.customizations?.primaryColor || theme.palette.primary.main}80`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              bottom: -10,
              right: -3,
              width: 15,
              height: 15,
              backgroundColor: 'inherit',
              transform: 'rotate(45deg)',
              borderRadius: 2
            }
          }}
          onClick={toggleMinimize}
        >
          <Avatar 
            sx={{ 
              width: 60, // Increased size
              height: 60, // Increased size
              backgroundColor: 'white',
              color: bot?.customizations?.primaryColor || theme.palette.primary.main,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 36 }} /> {/* Bigger icon */}
          </Avatar>
          
          {/* Removed the close button */}
          
          {/* Notification dot if there are unread messages */}
          {chatHistory.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                width: 15, // Bigger dot
                height: 15, // Bigger dot
                borderRadius: '50%',
                backgroundColor: '#ff5252',
                border: '2px solid white',
                animation: 'pulseDot 1.5s infinite',
              }}
            />
          )}
          
          {/* Animated chat dialog bubble */}
          <Box
            className="chat-prompt"
            sx={{
              position: 'absolute',
              top: -45,
              left: -150, // Changed from right: -80 to left: -150
              backgroundColor: 'white',
              color: 'rgba(0,0,0,0.8)',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              animation: 'chatBubbleAnimation 4s ease-in-out infinite',
              opacity: 1,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 'auto', // Reset left positioning
                right: 30, // Changed positioning to point to the badge from left side
                width: 16,
                height: 16,
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
                boxShadow: '2px 2px 3px rgba(0,0,0,0.05)'
              }
            }}
          >
            Ask me anything about {bot?.name || "our AI"}!
          </Box>
        </Box>
      ) : (
        // Full chat widget when not minimized
        <Box
          sx={{
            position: 'fixed',
            bottom: chatOpen ? 20 : -500,
            right: 20,
            width: expanded ? '90vw' : { xs: '95vw', sm: 480 },
            height: expanded ? '90vh' : { xs: '80vh', sm: 675 },
            borderRadius: 2,
            boxShadow: '0 4px 30px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            maxWidth: expanded ? '1500px' : '480px',
            maxHeight: expanded ? '900px' : '675px',
            zIndex: 1000,
            transform: chatOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
            opacity: chatOpen ? 1 : 0,
          }}
        >
          <ChatHeader 
            bot={bot} 
            minimized={minimized} 
            toggleMinimize={toggleMinimize} 
            toggleExpanded={toggleExpanded} 
            toggleChat={toggleChat}
          />
          
          <Collapse in={true} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '600px', position: 'relative' }}>
              <ChatMessages 
                chatHistory={chatHistory} 
                chatContainerRef={chatContainerRef} 
                sending={sending}
                bot={bot}
                formatResponse={(text) => text}
              />
              
              <ChatInput 
                message={message} 
                setMessage={setMessage} 
                handleSend={handleSend} 
                sending={sending} 
                bot={bot}
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

// Chat header component
const ChatHeader = ({ bot, minimized, toggleMinimize, toggleExpanded, toggleChat }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar 
          sx={{ 
            backgroundColor: 'white',
            color: bot?.customizations?.primaryColor || theme.palette.primary.main,
            mr: 2,
            animation: minimized ? 'none' : 'subtle-bounce 2s infinite ease-in-out'
          }}
        >
          {minimized ? (
            <Box
              component="img"
              src="/robot.svg"
              alt="Bot Logo"
              sx={{ width: 24, height: 24 }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/favicon.ico'; // Fallback to favicon
              }}
            />
          ) : (
            <SmartToyIcon />
          )}
        </Avatar>
        {!minimized && (
          <Box>
            <Typography variant="subtitle1">{bot?.name || 'AI Assistant'}</Typography>
            <Typography variant="caption">
              <Box component="span" sx={{ 
                display: 'inline-block', 
                width: 8, 
                height: 8, 
                bgcolor: '#2ecc71', 
                borderRadius: '50%', 
                mr: 0.8,
                animation: 'pulse 1.5s infinite'
              }} />
              Online • Ready to help
            </Typography>
          </Box>
        )}
      </Box>
      
      <Box>
        <IconButton 
          size="small" 
          onClick={toggleExpanded} 
          sx={{ color: 'white', mr: 0.5, display: minimized ? 'none' : 'inline-flex' }} 
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={toggleMinimize} 
          sx={{ color: 'white', mr: 0.5 }} 
        >
          <MinimizeIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={toggleChat} 
          sx={{ color: 'white' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

// Chat messages component
const ChatMessages = ({ chatHistory, chatContainerRef, sending, bot, formatResponse }) => {
  const theme = useTheme();
  
  return (
    <Box
      ref={chatContainerRef}
      sx={{ 
        flexGrow: 1,
        p: 2, 
        overflowY: 'auto',
        backgroundColor: '#f5f7fb',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        backgroundImage: 'radial-gradient(rgba(0,0,0,0.02) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}
    >
      {chatHistory.map((msg, index) => (
        <Box 
          key={index}
          sx={{ 
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 1,
            opacity: 0,
            transform: msg.role === 'user' ? 'translateX(20px)' : 'translateX(-20px)',
            animation: 'fadeInMessage 0.3s forwards',
            animationDelay: `${index * 0.1}s`,
          }}
        >
          {msg.role === 'bot' && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 1,
                bgcolor: bot?.customizations?.primaryColor || theme.palette.primary.main
              }}
            >
              <SmartToyIcon sx={{ fontSize: 16 }} />
            </Avatar>
          )}
          
          <Box
            sx={{
              maxWidth: '70%',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: msg.role === 'user' ? 'white' : bot?.customizations?.primaryColor || theme.palette.primary.main,
              color: msg.role === 'user' ? 'text.primary' : 'white',
              boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
              '& code': {
                backgroundColor: msg.role === 'user' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.2)',
                padding: '2px 4px',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.9em'
              },
              '& pre': {
                backgroundColor: msg.role === 'user' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.2)',
                padding: '8px',
                borderRadius: 1,
                overflow: 'auto',
                maxWidth: '100%'
              },
              position: 'relative',
              '&::after': msg.role === 'bot' ? {
                content: '""',
                position: 'absolute',
                left: '-5px',
                top: '10px',
                width: '10px',
                height: '10px',
                backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
                transform: 'rotate(45deg)',
              } : msg.role === 'user' ? {
                content: '""',
                position: 'absolute',
                right: '-5px',
                top: '10px',
                width: '10px',
                height: '10px',
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
              } : {},
            }}
            dangerouslySetInnerHTML={{ 
              __html: formatResponse(msg.content) 
            }}
          />
          
          {msg.role === 'user' && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                ml: 1,
                bgcolor: 'rgba(0,0,0,0.15)'
              }}
            >
              <Typography variant="body2">You</Typography>
            </Avatar>
          )}
        </Box>
      ))}
      
      {sending && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1, 
            opacity: 0,
            animation: 'fadeIn 0.3s forwards'
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              mr: 1,
              bgcolor: bot?.customizations?.primaryColor || theme.palette.primary.main
            }}
          >
            <SmartToyIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Box 
            sx={{ 
              p: 2,
              borderRadius: 2,
              backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: '-5px',
                top: '10px',
                width: '10px',
                height: '10px',
                backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
                transform: 'rotate(45deg)',
              }
            }}
          >
            <span className="typing-dot"></span>
            <span className="typing-dot"></span>
            <span className="typing-dot"></span>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Chat input component
const ChatInput = ({ message, setMessage, handleSend, sending, bot }) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        p: 2,  
        display: 'flex', 
        borderTop: '2px solid #eee', 
        bgcolor: 'white', 
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative circuit lines in the background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827zM5.373 0l-.83.828L5.96 2.243 8.2 0H5.374zM48.97 0l3.657 3.657-1.414 1.414L46.143 0h2.828zM11.03 0L7.372 3.657 8.787 5.07 13.857 0H11.03zm32.284 0L49.8 6.485 48.384 7.9l-7.9-7.9h2.83zM16.686 0L10.2 6.485 11.616 7.9l7.9-7.9h-2.83z' fill='%23000000' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
      />
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Type a message..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
        disabled={sending}
        size="small"
        InputProps={{
          startAdornment: (
            <ChatIcon color="action" sx={{ mr: 1, opacity: 0.6 }} />
          )
        }}
        autoComplete="off"
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(5px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.9)',
            },
            '&.Mui-focused': {
              backgroundColor: 'white',
            }
          }
        }}
      />
      <IconButton
        onClick={handleSend}
        disabled={!message.trim() || sending}
        color="primary"
        sx={{
          ml: 1,
          backgroundColor: bot?.customizations?.primaryColor || theme.palette.primary.main,
          color: 'white',
          '&:hover': {
            backgroundColor: bot?.customizations?.primaryColor 
              ? `${bot.customizations.primaryColor}DD` 
              : theme.palette.primary.dark,
            transform: 'scale(1.05)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0,0,0,0.12)',
            color: 'rgba(0,0,0,0.26)'
          },
          transition: 'all 0.2s ease',
        }}
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
};

export default ChatWidget;
